import React from "react";
import { ChevronDown, ListFilter, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export type AmenityStatusFilter = "all" | "true" | "false";

export interface AmenitiesFilterValues {
  status: AmenityStatusFilter;
  categories: string[];
}

export const DEFAULT_AMENITIES_FILTERS: AmenitiesFilterValues = {
  status: "all",
  categories: [],
};

interface AmenitiesFiltersPopoverProps {
  applied: AmenitiesFilterValues;
  categoryOptions: string[];
  onApply: (filters: AmenitiesFilterValues) => void;
}

type FilterSectionId = "status" | "category";

const FILTER_SECTION_TRIGGER_CLASS =
  "flex h-9 w-full items-center justify-between gap-2 rounded-md border border-base-border bg-white px-4 py-2 text-left text-sm font-medium leading-normal text-black/45 outline-none transition-colors hover:bg-secondary-badge focus-visible:ring-0";

const FILTER_SECTION_PANEL_CLASS =
  "rounded-md border border-base-border bg-white p-2 shadow-sm";

const isStatusFilter = (value: string): value is AmenityStatusFilter =>
  value === "all" || value === "true" || value === "false";

const getActiveFiltersCount = (filters: AmenitiesFilterValues): number =>
  [filters.status !== "all", filters.categories.length > 0].filter(Boolean)
    .length;

export const AmenitiesFiltersPopover: React.FC<
  AmenitiesFiltersPopoverProps
> = ({ applied, categoryOptions, onApply }) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<AmenitiesFilterValues>(applied);
  const [expandedSection, setExpandedSection] =
    React.useState<FilterSectionId | null>("status");

  const activeCount = getActiveFiltersCount(draft);
  const filtersTitle =
    activeCount > 0
      ? t("amenities.page.filters.titleWithCount", { count: activeCount })
      : t("amenities.page.filters.title");

  const toggleSection = (section: FilterSectionId): void => {
    setExpandedSection((previous) => (previous === section ? null : section));
  };

  const handleOpenChange = (nextOpen: boolean): void => {
    setOpen(nextOpen);
    setDraft(applied);
    setExpandedSection(
      applied.status === "all" && applied.categories.length > 0
        ? "category"
        : "status",
    );
  };

  const handleReset = (): void => {
    setDraft(DEFAULT_AMENITIES_FILTERS);
    setExpandedSection("status");
    onApply(DEFAULT_AMENITIES_FILTERS);
  };

  const handleApply = (): void => {
    onApply(draft);
    setOpen(false);
  };

  const toggleCategory = (category: string): void => {
    setDraft((previous) => ({
      ...previous,
      categories: previous.categories.includes(category)
        ? previous.categories.filter((item) => item !== category)
        : [...previous.categories, category],
    }));
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="icon-text"
          className="text-foreground border-base-border h-8 min-h-8 w-fit gap-2 border py-1 text-sm font-medium shadow-xs"
        >
          <ListFilter />
          {t("amenities.page.filters.button")}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={8}
        className="border-base-border flex max-h-[min(560px,calc(100vh-120px))] w-[320px] max-w-[calc(100vw-2rem)] flex-col gap-6 overflow-hidden rounded-2xl border bg-white p-4 shadow-md"
      >
        <div className="flex shrink-0 items-start justify-between gap-2">
          <h4 className="text-base leading-normal font-semibold text-black/96">
            {filtersTitle}
          </h4>
          <Button
            type="button"
            variant="span"
            className="hover:bg-secondary-badge rounded p-1 text-black/45 transition hover:text-black/96"
            onClick={() => setOpen(false)}
            aria-label={t("amenities.page.filters.close")}
          >
            <X className="size-5" />
          </Button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-0.5">
          <Button
            type="button"
            variant="span"
            className={FILTER_SECTION_TRIGGER_CLASS}
            onClick={() => toggleSection("status")}
            aria-expanded={expandedSection === "status"}
          >
            {t("amenities.page.status")}
            <ChevronDown
              className={cn(
                "shrink-0 text-black/45 transition-transform",
                expandedSection === "status" && "rotate-180",
              )}
            />
          </Button>
          {expandedSection === "status" ? (
            <div className={FILTER_SECTION_PANEL_CLASS}>
              <Select
                value={draft.status}
                onValueChange={(status) => {
                  if (isStatusFilter(status)) {
                    setDraft((previous) => ({ ...previous, status }));
                  }
                }}
              >
                <SelectTrigger size="sm" className="w-full justify-between">
                  <SelectValue placeholder={t("amenities.page.status")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="all">
                      {t("amenities.page.all")}
                    </SelectItem>
                    <SelectItem value="true">
                      {t("amenities.page.active")}
                    </SelectItem>
                    <SelectItem value="false">
                      {t("amenities.page.inactive")}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <Button
            type="button"
            variant="span"
            className={FILTER_SECTION_TRIGGER_CLASS}
            onClick={() => toggleSection("category")}
            aria-expanded={expandedSection === "category"}
          >
            {t("amenities.page.category")}
            <ChevronDown
              className={cn(
                "shrink-0 text-black/45 transition-transform",
                expandedSection === "category" && "rotate-180",
              )}
            />
          </Button>
          {expandedSection === "category" ? (
            <div
              className={cn(
                FILTER_SECTION_PANEL_CLASS,
                "flex max-h-56 flex-col gap-2 overflow-y-auto",
              )}
            >
              {categoryOptions.map((category) => {
                const id = `amenity-category-${category}`;
                return (
                  <label
                    key={category}
                    htmlFor={id}
                    className="flex cursor-pointer items-center gap-2 text-sm"
                  >
                    <Checkbox
                      id={id}
                      checked={draft.categories.includes(category)}
                      onCheckedChange={() => toggleCategory(category)}
                    />
                    {category}
                  </label>
                );
              })}
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2">
          <Button type="button" variant="new_outline" onClick={handleReset}>
            {t("amenities.page.filters.reset")}
          </Button>
          <Button type="button" variant="new_default" onClick={handleApply}>
            {t("amenities.page.filters.apply")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
